import { useState } from 'react'

import { ЕДИНИЦЫ_ДЛИНЫ, countUnits, ru } from '../../lib/physics'
import { Choice, Predict, useFrames } from './common'

/**
 * Чем измерить? (урок 3): измерение — сравнение с единицей. Единица шагает вдоль предмета,
 * счётчик считает шаги. Чем меньше единица, тем больше число, а длина та же.
 */

const ПРЕДМЕТЫ = [
  { имя: 'дверь', длина: 0.9 },
  { имя: 'тетрадь', длина: 0.2 },
  { имя: 'удав', длина: 3.8 },
] as const

const ДОРОЖКА = 300 // px на рисунке под предмет
const X0 = 30

export default function UnitsLab() {
  const [пред, setПред] = useState(2)
  const [ед, setЕд] = useState(2)
  const [шаг, setШаг] = useState(0) // сколько единиц уже отложено (дробное — анимация)
  const [идёт, setИдёт] = useState(false)

  const предмет = ПРЕДМЕТЫ[пред]
  const единица = ЕДИНИЦЫ_ДЛИНЫ[ед]
  const всего = countUnits(предмет.длина, единица.длина)
  const pxНаМ = ДОРОЖКА / предмет.длина // предмет — во всю ширину рисунка
  const длинаPx = предмет.длина * pxНаМ
  const единицаPx = единица.длина * pxНаМ

  useFrames((dt) => {
    if (!идёт) return
    setШаг((s) => {
      const n = s + dt * Math.max(2, всего / 4) // длинный счёт — быстрее
      if (n >= всего) {
        setИдёт(false)
        return всего
      }
      return n
    })
  })

  const мерить = () => {
    setШаг(0)
    setИдёт(true)
  }
  const сменить = (f: () => void) => {
    f()
    setШаг(0)
    setИдёт(false)
  }

  const целых = Math.floor(шаг + 1e-9)
  /** Целое — без запятой: 38, а не 38,0. */
  const число = (x: number) => ru(x, Math.abs(x - Math.round(x)) > 0.05 ? 1 : 0)
  /** «раз» или «раза»: 2 раза, 5 раз, 38 раз, 3,8 раза. */
  const раз = (x: number) => {
    if (Math.abs(x - Math.round(x)) > 0.05) return 'раза'
    const n = Math.round(x) % 100
    return n % 10 >= 2 && n % 10 <= 4 && (n < 12 || n > 14) ? 'раза' : 'раз'
  }
  const готово = !идёт && шаг > 0 && Math.abs(шаг - всего) < 1e-9

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 170" role="img" aria-label="Измерение длины единицей">
        {/* предмет */}
        {предмет.имя === 'удав' ? (
          <path
            d={`M${X0} 70 ${Array.from({ length: 13 }, (_, i) => `Q${X0 + (длинаPx / 12) * (i + 0.5)} ${i % 2 ? 58 : 82} ${X0 + (длинаPx / 12) * (i + 1)} 70`).join(' ')}`}
            className="ink-band" strokeWidth={10} fill="none" strokeLinecap="round"
          />
        ) : (
          <rect x={X0} y={предмет.имя === 'дверь' ? 50 : 60} width={длинаPx} height={предмет.имя === 'дверь' ? 40 : 20} rx={3} className="fill-body ink" strokeWidth={1.5} />
        )}
        <text x={X0} y={36} className="lab-label">{предмет.имя}: {ru(предмет.длина, предмет.длина < 1 ? 2 : 1)} м</text>
        {/* отложенные единицы */}
        {Array.from({ length: целых }, (_, i) => (
          <g key={i}>
            <rect x={X0 + i * единицаPx} y={100} width={единицаPx} height={14} className={i % 2 ? 'fill-unit-b' : 'fill-unit-a'} />
            {единицаPx > 14 && <text x={X0 + (i + 0.5) * единицаPx} y={111} textAnchor="middle" className="lab-tick">{i + 1}</text>}
          </g>
        ))}
        {шаг - целых > 0.001 && (
          <rect x={X0 + целых * единицаPx} y={100} width={(шаг - целых) * единицаPx} height={14} className="fill-unit-a" opacity={0.5} />
        )}
        {идёт && (
          <rect x={X0 + Math.min(шаг, всего - 1e-6) * единицаPx} y={122} width={единицаPx} height={10} rx={2} className="fill-heavy" opacity={0.7} />
        )}
        <line x1={X0} y1={95} x2={X0} y2={140} className="ink-muted" />
        <line x1={X0 + длинаPx} y1={95} x2={X0 + длинаPx} y2={140} className="ink-muted" />
      </svg>

      <div className="lab-big">
        {шаг > 0 ? (
          <>
            {предмет.имя}: <b>{число(шаг)}</b> {единица.имя}
          </>
        ) : (
          'Нажми «Измерить»'
        )}
      </div>
      {готово && (
        <div className="lab-caption">
          Единица «{единица.имя}» уложилась {число(всего)} {раз(всего)}. Измерить — значит сравнить с единицей.
        </div>
      )}

      <Predict
        question="Удав длиной 3,8 м. Сколько в нём попугаев (по 10 см)?"
        options={['3,8', '38', '380']}
        right={1}
        revealed={готово && предмет.имя === 'удав' && единица.имя === 'попугай'}
        explain="3,8 м = 380 см, а попугай — 10 см: 380 : 10 = 38. В мартышках удав короче — 5, хотя сам он не изменился."
      />

      <div className="lab-sub">Что измеряем</div>
      <Choice options={ПРЕДМЕТЫ.map((п, i) => ({ value: i, label: п.имя }))} value={пред} onChange={(v) => сменить(() => setПред(v))} />
      <div className="lab-sub">Чем измеряем</div>
      <Choice
        options={ЕДИНИЦЫ_ДЛИНЫ.map((u, i) => ({ value: i, label: `${u.имя} (${ru(u.длина * 100, 0)} см)` }))}
        value={ед}
        onChange={(v) => сменить(() => setЕд(v))}
      />
      <div className="row-wrap" style={{ marginTop: 12 }}>
        <button className="btn btn-primary" onClick={мерить}>📏 Измерить</button>
      </div>
      <p className="muted small">
        Пядь у каждого своя, попугаи и мартышки — тем более. Чтобы числа понимали все, учёные
        договорились о единицах: метр, секунда, килограмм — Международная система единиц (СИ).
      </p>
    </div>
  )
}
